import React from "react";
import { SUBJECT_SUGGESTIONS, CAPACITY_PRESETS, COLOR_VARIANTS } from "../studyGroupConfig";

const CreateGroupModal = ({
  isOpen,
  onClose,
  groupData,
  setGroupData,
  onSubmit,
  isSubmitting,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 md:p-8 animate-in zoom-in-95 duration-200 border border-gray-100 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-5">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Create Study Group</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Set up a collaborative study circle for your peers
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition flex items-center justify-center"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Group Name */}
          <div>
            <div className="flex items-center justify-between mb-1.5 ml-1">
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                Group Name <span className="text-red-500">*</span>
              </label>
              <span className="text-[11px] text-gray-400">{groupData.name.length}/60</span>
            </div>
            <input
              type="text"
              maxLength={60}
              value={groupData.name}
              onChange={(e) => setGroupData({ ...groupData, name: e.target.value })}
              placeholder="e.g. NCLEX Mastery Circle"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition bg-gray-50 focus:bg-white text-sm font-medium"
              required
            />
          </div>

          {/* Subject with suggestions */}
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5 ml-1">
              Subject / Category
            </label>
            <input
              type="text"
              maxLength={40}
              value={groupData.subject}
              onChange={(e) => setGroupData({ ...groupData, subject: e.target.value })}
              placeholder="e.g. Pharmacology"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition bg-gray-50 focus:bg-white text-sm font-medium mb-2"
            />
            <div className="flex flex-wrap gap-1.5 px-0.5">
              {SUBJECT_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setGroupData({ ...groupData, subject: s })}
                  className={`text-[11px] px-2.5 py-1 rounded-lg transition font-semibold ${
                    groupData.subject === s
                      ? "bg-blue-600 text-white shadow-xs"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Capacity Selector with Presets */}
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5 ml-1">
              Member Capacity (2 – 50)
            </label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                min="2"
                max="50"
                value={groupData.maxMembers}
                onChange={(e) =>
                  setGroupData({
                    ...groupData,
                    maxMembers: Math.min(50, Math.max(2, Number(e.target.value) || 2)),
                  })
                }
                className="w-24 px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition bg-gray-50 focus:bg-white text-sm font-bold text-center"
              />
              <div className="flex items-center gap-1.5 flex-1 overflow-x-auto">
                {CAPACITY_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setGroupData({ ...groupData, maxMembers: preset })}
                    className={`text-xs px-3 py-2 rounded-xl transition font-bold ${
                      Number(groupData.maxMembers) === preset
                        ? "bg-blue-600 text-white shadow-xs"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Color Theme Selector */}
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2 ml-1">
              Accent Color Theme
            </label>
            <div className="flex items-center gap-3 px-1">
              {Object.keys(COLOR_VARIANTS).map((colorKey) => {
                const theme = COLOR_VARIANTS[colorKey];
                const isSelected = groupData.color === colorKey;
                return (
                  <button
                    key={colorKey}
                    type="button"
                    onClick={() => setGroupData({ ...groupData, color: colorKey })}
                    className={`w-9 h-9 rounded-xl transition-all flex items-center justify-center ${
                      theme.pickerBg
                    } ${
                      isSelected
                        ? "ring-4 ring-offset-2 ring-blue-500 scale-110 shadow-md"
                        : "hover:scale-105 opacity-80 hover:opacity-100"
                    }`}
                    title={colorKey.toUpperCase()}
                  >
                    {isSelected && <i className="fa-solid fa-check text-white text-xs"></i>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-5 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !groupData.name.trim()}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-blue-600 hover:bg-blue-700 transition shadow-md disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-xs"></i>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-check text-xs"></i>
                  <span>Create Group</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupModal;
